import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from 'redux/store/user/getters'
import { getInvoiceDataTemplate, getRejectedInvoicesAmount } from '../api'
import { getRejectedAmount } from '../../../redux/store/rejectedInvoices/getters'
import { setRejectedAmount } from '../../../redux/store/rejectedInvoices/slice'
import useTabs, {
  TabsReturnActions,
  TabsReturnState
} from '../../../hooks/useTabs'
import { ROLES } from 'features/Permission'
import { INVOICE_DATA_TABS } from '../../../features/InvoiceData/constants'
import { TRFPDocument } from '../../../features/RFP/RFPDetails/types'
import { TInvoiceDataResult } from '../../../features/InvoiceData/types'

type ContextProps = {
  state: {
    rejectedAmount: number
    files: TRFPDocument[]
  } & TabsReturnState
  actions: {} & TabsReturnActions
}
const InvoiceDataContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const InvoiceDataContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const rejectedAmount = useSelector(getRejectedAmount)
  const [files, setFiles] = useState<TInvoiceDataResult[]>([])
  const { tab } = useParams()
  const TABS = useMemo(
    () => INVOICE_DATA_TABS(rejectedAmount),
    [rejectedAmount]
  )

  const { state: tabsState, actions: tabsActions } = useTabs({
    tabs: TABS,
    activeTab: tab || TABS[0].key,
    updateTrigger: rejectedAmount
  })

  useEffect(() => {
    onGetRejectedInvoicesAmount()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!!tab) tabsActions.setActiveTab(tab)
    // eslint-disable-next-line
  }, [tab])

  useEffect(() => {
    if (user.role !== ROLES.COMMUNITY_PRESIDENT)
      getInvoiceDataTemplate().then((res) =>
        setFiles(
          res.data.results.map((i, idx) => ({
            ...i,
            uuid: `${idx}`
          }))
        )
      )
    // eslint-disable-next-line
  }, [dispatch])

  const context = useMemo(
    () => ({
      state: {
        files,
        rejectedAmount,
        ...tabsState
      },
      actions: { ...tabsActions }
    }),
    [files, tabsState, tabsActions, rejectedAmount]
  )

  const onGetRejectedInvoicesAmount = async () => {
    const { data } = await getRejectedInvoicesAmount()
    dispatch(setRejectedAmount(data.count))
  }

  return (
    <InvoiceDataContext.Provider value={context}>
      {children}
    </InvoiceDataContext.Provider>
  )
}

export const useInvoiceDataContext = () => useContext(InvoiceDataContext)

export default InvoiceDataContextProvider
